import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Button,
  Form,
  H4,
  H5,
  IconArrowHead,
  IconVerifiedCheck,
  IconBillList,
  IconSupportChat,
  IconPhone,
} from '../..';
import moment from 'moment';
import agentImage from './agentImage.png';

// Import modules from this directory
import css from './PackagesForm.module.css';

/**
 * The PackagesForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.formId] - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} [props.disabled] - Whether the form is disabled
 * @param {boolean} [props.ready] - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {boolean} [props.invalid] - Whether the form is invalid
 * @param {boolean} [props.pristine] - Whether the form is pristine
 * @returns {JSX.Element}
 */
export const PackagesForm = props => (
  <FinalForm
    mutators={{ ...arrayMutators }}
    {...props}
    render={formRenderProps => {
      const {
        formId = 'PackagesForm',
        form: formApi,
        className,
        rootClassName,
        disabled,
        ready,
        handleSubmit,
        invalid,
        pristine,
        initialValues: formInitialValues,
        values: formValues,
        intl,
        data,
      } = formRenderProps;

      const threeMonthsLater = moment()
        .add(3, 'months')
        .format('DD-MM-YYYY');
      const nineMonthsLater = moment()
        .add(9, 'months')
        .format('DD-MM-YYYY');
      const fourteenMonthsLater = moment()
        .add(14, 'months')
        .format('DD-MM-YYYY');
      const { monthlyHours } = data || {};
      const { monthlyTutoringHours = 4 } = monthlyHours || {};
      const basePrice = 41.85;
      const basePriceFormatted = basePrice.toFixed(2);
      const proPackagePrice = basePrice - basePrice * 0.25;
      const proPackagePriceFormatted = proPackagePrice.toFixed(2);
      const premiumPackagePrice = basePrice - basePrice * 0.3;
      const premiumPackagePriceFormatted = premiumPackagePrice.toFixed(2);

      const basicPackageHours = monthlyTutoringHours * 3;
      const proPackageHours = monthlyTutoringHours * 9;
      const premiumPackageHours = monthlyTutoringHours * 14;

      const packages = [
        {
          name: 'basic',
          header: intl.formatMessage({ id: 'PackagesForm.title' }, { hours: basicPackageHours }),
          description: intl.formatMessage({ id: 'PackagesForm.basic.description' }),
          expiresAt: intl.formatMessage(
            { id: 'PackagesForm.expiresAt' },
            { expiresAt: threeMonthsLater }
          ),
          expiresAtDate: threeMonthsLater,
          hours: basicPackageHours,
          price: intl.formatMessage({ id: 'PackagesForm.price' }, { price: basePriceFormatted }),
          priceValue: basePrice,
          cta: intl.formatMessage({ id: 'PackagesForm.select' }),
        },
        {
          name: 'pro',
          header: intl.formatMessage({ id: 'PackagesForm.title' }, { hours: proPackageHours }),
          description: intl.formatMessage({ id: 'PackagesForm.pro.description' }),
          expiresAt: intl.formatMessage(
            { id: 'PackagesForm.expiresAt' },
            { expiresAt: nineMonthsLater }
          ),
          expiresAtDate: nineMonthsLater,
          hours: proPackageHours,
          price: intl.formatMessage(
            { id: 'PackagesForm.price' },
            { price: proPackagePriceFormatted }
          ),
          priceValue: proPackagePrice,
          cta: intl.formatMessage({ id: 'PackagesForm.select' }),
          discountPercentage: 25,
        },
        {
          name: 'premium',
          header: intl.formatMessage({ id: 'PackagesForm.title' }, { hours: premiumPackageHours }),
          description: intl.formatMessage({ id: 'PackagesForm.premium.description' }),
          expiresAt: intl.formatMessage(
            { id: 'PackagesForm.expiresAt' },
            { expiresAt: fourteenMonthsLater }
          ),
          expiresAtDate: fourteenMonthsLater,
          hours: premiumPackageHours,
          price: intl.formatMessage(
            { id: 'PackagesForm.price' },
            { price: premiumPackagePriceFormatted }
          ),
          priceValue: premiumPackagePrice,
          cta: intl.formatMessage({ id: 'PackagesForm.select' }),
          discountPercentage: 30,
        },
      ];

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = ready;
      const submitDisabled = invalid || disabled;

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          <H4 className={css.title}>
            <FormattedMessage id="PackagesModal.PackagesForm.title" />
          </H4>
          <div className={css.label}>
            <FormattedMessage id="PackagesModal.PackagesForm.choosePackage" />
          </div>

          <div className={css.packages}>
            {packages.map((p, i) => (
              <div key={i} className={css.package}>
                <div className={css.header}>
                  <H5 className={css.heading}>{p.header}</H5>
                  <div className={css.description}>{p.description}</div>
                </div>
                <div className={css.expiresAt}>{p.expiresAt}</div>
                <div className={css.price}>
                  <div className={css.priceWrapper}>
                    <span className={css.priceValue}>{p.price}</span>
                    <span className={css.priceUnit}>
                      {intl.formatMessage({ id: 'PackagesForm.priceUnit' })}
                    </span>
                  </div>
                  {!!p.discountPercentage && (
                    <div className={css.discount}>
                      {intl.formatMessage(
                        { id: 'PackagesForm.discountPercentage' },
                        { discountPercentage: p.discountPercentage }
                      )}
                    </div>
                  )}
                </div>
                <div className={css.ctaContainer}>
                  <Button
                    className={css.ctaButton}
                    onClick={() => {
                      formApi.batch(() => {
                        const subTotal = basePrice * p.hours;
                        const total = p.priceValue * p.hours;
                        formApi.change('package', p.name);
                        formApi.change('expiresAt', p.expiresAtDate);
                        formApi.change('subTotal', subTotal);
                        formApi.change('total', total);
                        formApi.change('hours', p.hours);
                      });
                    }}
                  >
                    {p.cta}
                    <IconArrowHead direction="right" size="small" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className={css.notes}>
            <div className={css.note}>
              <IconVerifiedCheck className={css.iconVerifiedCheck} />
              <FormattedMessage id="PackagesModal.PackagesForm.cancellationNote" />
            </div>
            <div className={css.note}>
              <IconBillList className={css.iconUserHeart} />
              <FormattedMessage id="PackagesModal.PackagesForm.cancellationNoteWithNotice" />
            </div>
          </div>

          <div className={css.support}>
            <div className={css.agent}>
              <div className={css.agentImage}>
                <img src={agentImage} alt="Agent Image" />
              </div>
              <div className={css.agentDetails}>
                <H5 className={css.agentName}>
                  <FormattedMessage id="PackagesModal.PackagesForm.agentName" />
                </H5>
                <div className={css.agentPosition}>
                  <FormattedMessage id="PackagesModal.PackagesForm.agentPosition" />
                </div>
              </div>
            </div>
            <div className={css.actionBtns}>
              <Button
                className={css.chat}
                onClick={e => {
                  e.preventDefault();
                }}
              >
                <IconSupportChat className={css.iconChat} />
                <FormattedMessage id="PackagesModal.PackagesForm.chat" />
              </Button>
              <Button
                className={css.contact}
                onClick={e => {
                  e.preventDefault();
                }}
              >
                <IconPhone className={css.iconPhone} />
                <FormattedMessage id="PackagesModal.PackagesForm.contact" />
              </Button>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

export default PackagesForm;
